import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { resetPassword } from '../../services/api';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Lock, ArrowBack, Visibility, VisibilityOff } from '@mui/icons-material';

/* ── Design tokens ─────────────────────────────────────────────── */
const PRIMARY = '#6C98AD';
const PRIMARY_HOVER = '#5a8499';
const SECONDARY = '#24272a';
const CARD_SHADOW = '0 4px 20px rgba(108, 152, 173, 0.15)';
const CARD_SHADOW_HOVER = '0 8px 30px rgba(108, 152, 173, 0.25)';
const FONT_HEADING = "'Avenir Next LT Pro', sans-serif";
const FONT_BODY = "'Avenir Next LT Pro', sans-serif";
const RADIUS = '1rem';
const TRANSITION = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  const validatePassword = (password) => {
    if (!password) {
      return 'Password is required';
    }
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    return '';
  };

  const validateConfirmPassword = (confirmPass) => {
    if (!confirmPass) {
      return 'Please confirm your password';
    }
    if (confirmPass !== password) {
      return 'Passwords do not match';
    }
    return '';
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    if (passwordError) {
      setPasswordError(validatePassword(value));
    }
    // Re-validate confirm password if it has a value
    if (confirmPassword && confirmPasswordError) {
      setConfirmPasswordError(value !== confirmPassword ? 'Passwords do not match' : '');
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (confirmPasswordError) {
      setConfirmPasswordError(validateConfirmPassword(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate all fields
    const passErr = validatePassword(password);
    const confirmPassErr = validateConfirmPassword(confirmPassword);

    setPasswordError(passErr);
    setConfirmPasswordError(confirmPassErr);

    if (passErr || confirmPassErr) {
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, password, confirmPassword);
      navigate('/admin/login', { 
        state: { message: 'Password reset successfully. Please login with your new password.' } 
      });
    } catch (err) {
      setError(err.message || 'Invalid or expired reset token');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: `url(${process.env.PUBLIC_URL}/static/images/mstructure_login_bg.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        fontFamily: FONT_BODY,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          pointerEvents: 'none',
        },
      }}
    >
      <Paper
        component="form"
        onSubmit={handleSubmit}
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          width: '100%',
          maxWidth: 440,
          borderRadius: RADIUS,
          boxShadow: CARD_SHADOW,
          border: '2px solid rgba(108, 152, 173, 0.15)',
          transition: TRANSITION,
          position: 'relative',
          zIndex: 1,
          backgroundColor: '#ffffff',
          '&:hover': {
            boxShadow: CARD_SHADOW_HOVER,
            borderColor: PRIMARY,
          },
        }}
      >
        {/* Logo + heading */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <img
            src={process.env.PUBLIC_URL + '/TALOGOWEBSITE.webp'}
            alt="Tailored Alloys"
            height="40"
            style={{ marginBottom: 16 }}
          />
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: SECONDARY, fontFamily: FONT_HEADING }}
          >
            Reset Password
          </Typography>
          <Typography variant="body2" sx={{ color: PRIMARY, mt: 0.5, fontFamily: FONT_BODY }}>
            Enter your new password below
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: RADIUS }}>
            {error}
          </Alert>
        )}

        <TextField
          label="New Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={handlePasswordChange}
          onBlur={() => setPasswordError(validatePassword(password))}
          fullWidth
          required
          error={!!passwordError}
          helperText={passwordError || 'Min 8 chars, uppercase, lowercase, and number required'}
          sx={{
            mb: 2.5,
            '& .MuiOutlinedInput-root': {
              borderRadius: RADIUS,
              fontFamily: FONT_BODY,
              '&.Mui-focused fieldset': { borderColor: PRIMARY },
            },
            '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock sx={{ color: PRIMARY }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  sx={{ color: PRIMARY }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Confirm Password"
          type={showConfirmPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={handleConfirmPasswordChange}
          onBlur={() => setConfirmPasswordError(validateConfirmPassword(confirmPassword))}
          fullWidth
          required
          error={!!confirmPasswordError}
          helperText={confirmPasswordError}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              borderRadius: RADIUS,
              fontFamily: FONT_BODY,
              '&.Mui-focused fieldset': { borderColor: PRIMARY },
            },
            '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock sx={{ color: PRIMARY }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                  sx={{ color: PRIMARY }}
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loading}
          sx={{
            py: 1.4,
            borderRadius: RADIUS,
            fontWeight: 600,
            fontSize: '1rem',
            fontFamily: FONT_BODY,
            textTransform: 'none',
            background: `linear-gradient(135deg, ${PRIMARY} 0%, #8eb4c7 100%)`,
            boxShadow: '0 4px 12px rgba(108, 152, 173, 0.3)',
            transition: TRANSITION,
            '&:hover': {
              background: `linear-gradient(135deg, ${PRIMARY_HOVER} 0%, ${PRIMARY} 100%)`,
              boxShadow: '0 8px 20px rgba(108, 152, 173, 0.5)',
              transform: 'translateY(-2px)',
            },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
        </Button>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Link
            to="/admin/login"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              color: PRIMARY,
              textDecoration: 'none',
              fontFamily: FONT_BODY,
              fontSize: '0.9rem',
            }}
          >
            <ArrowBack sx={{ fontSize: 18 }} /> Back to Login
          </Link>
        </Box>
      </Paper>
    </Box>
  );
}
